
import { IAccount } from '@share/common-types';
import isEmpty from 'lodash/isEmpty';
import { ILoginState } from '@share/store/slices';

export const GetPreferenceValue = (account: IAccount, key: string, defaultValue: any = null) => {
  if (!isEmpty(account?.preferences)) {
    const prefDB: any = account?.preferences.find(p => p.key === key);
    if (prefDB) {
      return prefDB.value;
    }
  }
  return defaultValue;
}

export const GetPreferenceValueParsed = (account: IAccount, key: string, defaultValue: any = null) => {
  const pref = GetPreferenceValue(account, key, defaultValue);
  if (pref) {
    return JSON.parse(pref);
  }
  return null;
}

// Resolves the wallet profile that applies to the current org/site from a raw profiles
// list: the profile matching the org/site, otherwise the account-wide default profile
// (no org/site). Takes the list rather than the account so it can also be used at
// navigation-menu time, before the profiles are attached to the account.
export const ResolveWalletProfile = (
  profiles?: any[] | null,
  organizationId?: number | string,
  siteId?: number | string,
): any => {
  if (!profiles || !profiles.length) {
    return null;
  }

  const specificProfile = profiles.find(
    (p: any) =>
      p?.organizationId?.toString() === organizationId?.toString() &&
      p?.siteID?.toString() === siteId?.toString(),
  );
  const defaultProfile = profiles.find((p: any) => !p?.organizationId && !p?.siteID);

  return specificProfile ?? defaultProfile ?? null;
};

// True when the resolved wallet profile opts out of forcing wallet bookings
// non-refundable (`WalletProfile.SkipNonRefundable`). With it on, the provider's real
// refundability and cancellation policies apply to a booking paid with points, so the
// UI must not override them locally.
export const ResolveWalletSkipNonRefundable = (
  profiles?: any[] | null,
  organizationId?: number | string,
  siteId?: number | string,
): boolean => {
  return !!ResolveWalletProfile(profiles, organizationId, siteId)?.skipNonRefundable;
};

// Resolves the wallet profile that applies to the current user: the profile that
// matches the current org/site, otherwise the account-wide default profile (no
// org/site). Internal (regular) wallets only — external wallets never use profiles.
const GetWalletProfileForUser = (
  account?: IAccount,
  organizationId?: number | string,
  siteId?: number | string,
): any => {
  return ResolveWalletProfile(account?.walletProfiles, organizationId, siteId);
};

// The effective wallet percentage (whole-percent units, e.g. 10 = 10%) used both for
// the "Eligible {clientCashName} (X %)" label and to compute the earned amount.
// A per-profile `percentage` override wins over the account-level value whenever it is
// set to a valid number >= 0 — including 0, which is a valid override (it disables
// earnings). Only null / undefined / empty falls back to the account level. External
// wallets always use the account-level percentage.
export const GetEffectiveClientCashPercentage = (loginStore: ILoginState): number => {
  const account = loginStore?.account as IAccount;
  const siteId = loginStore?.user?.userAdditionalData?.siteID;
  const organizationId = loginStore?.user?.userAdditionalData?.organizationId;

  const accountPercentage =
    account?.clientCashPercentage && !isEmpty(account.clientCashPercentage)
      ? parseInt(account.clientCashPercentage)
      : 0;

  if (account?.walletIsExternal) {
    return accountPercentage;
  }

  const profile = GetWalletProfileForUser(account, organizationId, siteId);
  const override = profile?.percentage;

  const hasOverride =
    override !== null &&
    override !== undefined &&
    `${override}`.trim() !== '' &&
    Number.isFinite(Number(override));

  if (hasOverride && Number(override) >= 0) {
    return Number(override);
  }

  return accountPercentage;
};

export const GetMultipleCitiesEnabled = (loginStore: ILoginState) => {

  const siteId = loginStore?.user?.userAdditionalData?.siteID;
  const organizationId = loginStore?.user?.userAdditionalData?.organizationId;
  const account = loginStore?.account as IAccount;

  const toNumberArray = (val?: string): number[] =>val?.split(',').map(v => Number(v.trim())).filter(Boolean) ?? [];
  const hasMultipleCities = GetPreferenceValue(account, 'enable_share_compare_multiple_cities') === 'true';
  const orgIds = toNumberArray(GetPreferenceValue(account, 'enable_share_compare_multiple_cities_orgids'));
  const siteIds = toNumberArray(GetPreferenceValue(account, 'enable_share_compare_multiple_cities_siteids'));
      
  const MultipleCitiesEnable = orgIds.length && siteIds.length
        ? orgIds.includes(organizationId as number) && siteIds.includes(siteId as number)
        : orgIds.length
        ? orgIds.includes(organizationId as number)
        : siteIds.length
        ? siteIds.includes(siteId as number)
        : true;
  const hasMultipleCitiesEnable= hasMultipleCities && MultipleCitiesEnable; 
  return hasMultipleCitiesEnable ? true : false;
}

export const GetMultipleQuotesEnabled = (loginStore: ILoginState) => {

  const siteId = loginStore?.user?.userAdditionalData?.siteID;
  const organizationId = loginStore?.user?.userAdditionalData?.organizationId;
  const account = loginStore?.account as IAccount;

  const toNumberArray = (val?: string): number[] =>val?.split(',').map(v => Number(v.trim())).filter(Boolean) ?? [];
  const hasMultipleQuotes = GetPreferenceValue(account, 'enable_share_compare_multiple_quotes') === 'true';
  const orgIds = toNumberArray(GetPreferenceValue(account, 'enable_share_compare_multiple_quotes_orgids'));
  const siteIds = toNumberArray(GetPreferenceValue(account, 'enable_share_compare_multiple_quotes_siteids'));
      
  const MultipleQuotesEnable = orgIds.length && siteIds.length
        ? orgIds.includes(organizationId as number) && siteIds.includes(siteId as number)
        : orgIds.length
        ? orgIds.includes(organizationId as number)
        : siteIds.length
        ? siteIds.includes(siteId as number)
        : true;
  const hasMultipleQuotesEnable= hasMultipleQuotes && MultipleQuotesEnable; 
  return hasMultipleQuotesEnable ? true : false;
}
